import { useEffect, useState } from "react";
import { FilePdf, Files, WhatsappLogo, Copy, QrCode, Barcode, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { fmtBRL, fmtDate } from "../lib/format";
import { openWhatsApp } from "../lib/whatsapp";
import { downloadBlob } from "../lib/download";
import { ItfBarcode } from "./ItfBarcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const STATUS_LABEL = { REGISTRADO: "Em carteira", PAGO: "Liquidado", BAIXADO: "Baixado" };
const STATUS_CLS = {
  REGISTRADO: "text-sky-300 bg-sky-400/10 border-sky-400/30",
  PAGO: "text-emerald-300 bg-emerald-400/10 border-emerald-400/30",
  BAIXADO: "text-zinc-400 bg-zinc-400/10 border-zinc-400/30",
};

// PDF único com todas as parcelas do conjunto (pedido ou avulso parcelado) do boleto — um boleto por página
export const downloadSetPdf = async (boleto) => {
  try {
    const resp = await api.get(`/boletos/set/${boleto.id}/pdf`, { responseType: "blob" });
    const slug = boleto.order_code ? `pedido_${boleto.order_code}` : `grupo_${boleto.group_code || boleto.code}`;
    downloadBlob(resp.data, `boletos_${slug}_${boleto.client_name || ""}.pdf`.replace(/\s+/g, "_"));
    const n = resp.headers?.["x-boletos-count"];
    toast.success(n ? `PDF único com ${n} boleto(s) baixado` : "PDF único baixado");
    return true;
  } catch (e) {
    toast.error("Não foi possível gerar o PDF único das parcelas");
    return false;
  }
};

export const downloadBoletoPdf = async (boleto) => {
  try {
    const resp = await api.get(`/boletos/${boleto.id}/pdf`, { responseType: "blob" });
    downloadBlob(resp.data, `boleto_${boleto.code}_${boleto.client_name || ""}.pdf`.replace(/\s+/g, "_"));
    return true;
  } catch (e) {
    toast.error("Não foi possível gerar o PDF do boleto");
    return false;
  }
};

export const boletoMessage = (b) => [
  "*BOLETO SICREDI*" + (b.simulado ? " (SIMULAÇÃO — sem valor)" : ""),
  "",
  `Cliente: ${(b.client_name || "—").trim()}`,
  b.order_code ? `Pedido #${b.order_code} — parcela ${b.parcela}` : `Cobrança avulsa${b.group_code ? ` — parcela ${b.parcela}` : ""}${b.description ? ` — ${b.description}` : ""}`,
  `Vencimento: ${fmtDate(b.due_date)}`,
  `Valor: ${fmtBRL(b.amount)}`,
  "",
  "Linha digitável:",
  b.linha_digitavel || "—",
  ...(b.qr_code ? ["", "Pix copia e cola (mesma cobrança):", b.qr_code] : []),
  "",
  "O PDF do boleto segue em anexo. Obrigado!",
].join("\n");

const copy = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copiado`);
  } catch (e) {
    toast.error("Não foi possível copiar");
  }
};

// boleto: objeto completo (GET /boletos/{id}) ou parcial {id} — o dialog completa via API
export const BoletoResultDialog = ({ boleto, onClose, title = "Boleto emitido" }) => {
  const [b, setB] = useState(null);
  const [qr, setQr] = useState(null);

  useEffect(() => {
    if (!boleto) { setB(null); setQr(null); return; }
    const need = !boleto.linha_digitavel || !boleto.qr_code;
    (need ? api.get(`/boletos/${boleto.id}`).then((r) => r.data) : Promise.resolve(boleto))
      .then((full) => {
        setB(full);
        if (full.qr_code) {
          api.get(`/boletos/${full.id}/qrcode.svg`, { responseType: "blob" })
            .then((r) => setQr(URL.createObjectURL(r.data))).catch(() => setQr(null));
        }
      })
      .catch(() => toast.error("Não foi possível carregar o boleto"));
  }, [boleto]);

  const sendWhatsApp = async () => {
    await downloadBoletoPdf(b);
    const opened = openWhatsApp(b.client_phone, boletoMessage(b));
    if (!opened) { toast.error("O navegador bloqueou a janela — permita popups para abrir o WhatsApp"); return; }
    toast.success(b.client_phone ? "WhatsApp aberto com os dados do boleto — anexe o PDF baixado" : "Cliente sem telefone — escolha o contato no WhatsApp e anexe o PDF baixado");
  };

  return (
    <Dialog open={!!boleto} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-[95vw] sm:max-w-2xl max-h-[92vh] overflow-y-auto overflow-x-hidden" data-testid="boleto-result-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Barcode size={20} className="text-emerald-400" /> {title}{b ? ` — #${b.code}` : ""}
            {b && <span data-testid="boleto-result-status" className={`ml-2 text-xs font-medium px-2 py-0.5 border rounded ${STATUS_CLS[b.status] || ""}`}>{STATUS_LABEL[b.status] || b.status}</span>}
            {b?.simulado && <span className="text-[10px] uppercase text-amber-400 border border-amber-500/40 rounded px-1.5 py-0.5">simulação</span>}
          </DialogTitle>
        </DialogHeader>
        {!b ? <p className="text-sm text-zinc-500 py-6 text-center">Carregando...</p> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm border border-zinc-800 rounded-lg p-3">
              <div><p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Cliente</p><p className="text-zinc-200 truncate" title={b.client_name}>{b.client_name}</p></div>
              <div><p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Origem</p><p className="text-zinc-200">{b.order_code ? `Pedido #${b.order_code} · ${b.parcela}` : b.group_code ? `Avulso parcelado #${b.group_code} · ${b.parcela}` : "Avulso"}</p></div>
              <div><p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Vencimento</p><p className="text-zinc-200">{fmtDate(b.due_date)}</p></div>
              <div><p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Valor</p><p className="text-emerald-300 font-semibold" data-testid="boleto-result-amount">{fmtBRL(b.amount)}</p></div>
            </div>

            <div className="bg-white rounded-lg p-4 text-zinc-900">
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Linha digitável</p>
                <button data-testid="boleto-copy-linha" onClick={() => copy(b.linha_digitavel, "Linha digitável")}
                  className="inline-flex items-center gap-1 text-xs text-indigo-700 hover:text-indigo-900"><Copy size={13} /> copiar</button>
              </div>
              <p data-testid="boleto-result-linha" className="font-mono text-base sm:text-lg font-semibold tracking-wide break-all">{b.linha_digitavel}</p>
              <div className="mt-3">
                <ItfBarcode code={b.codigo_barras} height={64} testId="boleto-result-barcode" />
                <p className="font-mono text-[11px] text-zinc-500 mt-1 text-center tracking-widest">{b.codigo_barras}</p>
              </div>
              <p className="text-[11px] text-zinc-500 mt-1">Nosso número {b.nosso_numero_fmt || b.nosso_numero} · Nº documento {b.seu_numero}</p>
            </div>

            {b.qr_code && (
              <div className="flex flex-col sm:flex-row gap-4 items-start border border-zinc-800 rounded-lg p-3">
                <div className="bg-white rounded p-2 shrink-0">
                  {qr ? <img data-testid="boleto-result-qr" src={qr} alt="QR Code Pix" className="w-36 h-36" /> : <div className="w-36 h-36 flex items-center justify-center text-zinc-400"><QrCode size={40} /></div>}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-zinc-200 flex items-center gap-2"><QrCode size={16} className="text-emerald-400" /> Pix (boleto híbrido) — mesma cobrança</p>
                  <p className="text-xs text-zinc-500 mb-2">O cliente pode pagar pelo QR Code ou pelo código de barras; o Sicredi concilia os dois na mesma cobrança.</p>
                  <p className="font-mono text-[11px] text-zinc-400 break-all max-h-16 overflow-hidden">{b.qr_code}</p>
                  <button data-testid="boleto-copy-pix" onClick={() => copy(b.qr_code, "Pix copia e cola")}
                    className="mt-2 inline-flex items-center gap-1 text-xs text-indigo-300 hover:text-indigo-200"><Copy size={13} /> copiar Pix copia e cola</button>
                </div>
              </div>
            )}

            {b.status === "PAGO" && (
              <p className="text-xs text-emerald-300 flex items-center gap-1.5"><CheckCircle size={14} /> Liquidado em {fmtDate(b.paid_at)} ({b.paid_via}) — {fmtBRL(b.paid_value)}</p>
            )}

            <div className="flex flex-wrap justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="text-sm text-zinc-400 hover:text-zinc-200 px-3 py-2">Fechar</button>
              <button data-testid="boleto-result-pdf" onClick={() => downloadBoletoPdf(b)}
                className="inline-flex items-center gap-2 text-sm text-zinc-100 border border-zinc-600 hover:bg-zinc-800 px-4 py-2 rounded transition-colors">
                <FilePdf size={16} /> {b.set_count > 1 ? "PDF desta parcela" : "Baixar PDF"}
              </button>
              {b.set_count > 1 && (
                <button data-testid="boleto-result-set-pdf" onClick={() => downloadSetPdf(b)} title="Um único arquivo, um boleto por página, em ordem de vencimento"
                  className="inline-flex items-center gap-2 text-sm text-zinc-100 border border-indigo-500/60 bg-indigo-500/10 hover:bg-indigo-500/20 px-4 py-2 rounded transition-colors">
                  <Files size={16} /> PDF único ({b.set_count} parcelas)
                </button>
              )}
              <button data-testid="boleto-result-whatsapp" onClick={sendWhatsApp}
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium px-4 py-2 rounded transition-colors">
                <WhatsappLogo size={17} weight="fill" /> Enviar ao cliente
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
